import { useEffect, useState, type RefObject } from 'react';

/**
 * Marks a section as dark ground for the sticky tab band.
 *
 * A section opts in rather than the band keeping a list of them: the sections
 * that are dark already say so in their own classes, and a list here would be
 * a second copy of that fact for someone to forget to update.
 */
export const NAV_DARK_GROUND = { 'data-nav-ground': 'dark' } as const;

const DARK_GROUND_SELECTOR = '[data-nav-ground="dark"]';

/**
 * Whether the sticky tab band is currently sitting over a dark section.
 *
 * The band is sticky and the page scrolls under it, so its palette cannot be
 * decided once per tab — a profile's dark About and Selected Work sections pass
 * beneath the same band that was over the grey summary a moment earlier. Left
 * light throughout, the band reads as a pale stripe laid across the dark
 * sections, and the white pane marking the current tab is the brightest thing
 * on the page.
 *
 * Measured against the band's own midline rather than the viewport top, so it
 * answers for the strip the band actually occupies.
 */
export const useBandOverDark = (ref: RefObject<HTMLElement | null>) => {
    const [overDark, setOverDark] = useState(false);

    useEffect(() => {
        const band = ref.current;
        if (!band) return;

        // Measured straight out of the scroll handler rather than deferred
        // to a frame. It is two or three getBoundingClientRect calls against
        // elements already in the layout tree, which is cheaper than the
        // bookkeeping around deferring it — and a frame callback does not run
        // at all while the tab is hidden, which left the band showing the
        // ground it was over whenever the reader came back to a restored
        // scroll position.
        const measure = () => {
            const strip = band.getBoundingClientRect();
            const midline = strip.top + strip.height / 2;
            const grounds = document.querySelectorAll(DARK_GROUND_SELECTOR);
            for (const ground of grounds) {
                const rect = ground.getBoundingClientRect();
                if (rect.top <= midline && rect.bottom >= midline) {
                    setOverDark(true);
                    return;
                }
            }
            setOverDark(false);
        };

        measure();
        // Again once the browser has settled: a restored scroll position is
        // applied after this effect runs, so the first read can be taken with
        // the page still at the top.
        const settled = window.setTimeout(measure, 0);
        window.addEventListener('scroll', measure, { passive: true });
        window.addEventListener('resize', measure);

        return () => {
            window.clearTimeout(settled);
            window.removeEventListener('scroll', measure);
            window.removeEventListener('resize', measure);
        };
    }, [ref]);

    return overDark;
};
